// frontend/src/components/crm/AudienceBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { audiencesAPI, clientAPI, leadAPI, accountAPI, contactAPI } from '../../utils/crmApi';
import {
    UsersIcon,
    FunnelIcon,
    PlusIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    ArrowUpTrayIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Helper function to safely convert contactId to string
const safeContactIdToString = (contactId) => {
    if (!contactId) return '';
    
    // If it's already a string, validate it's not "[object Object]"
    if (typeof contactId === 'string') {
        return contactId !== '[object Object]' ? contactId : '';
    }
    
    // If it's an object, try to convert it properly
    if (typeof contactId === 'object') {
        // Check if it's a Mongoose ObjectId-like object with toString
        if (contactId.toString && typeof contactId.toString === 'function') {
            const str = contactId.toString();
            return str !== '[object Object]' ? str : '';
        }
        // Try to get _id if it exists
        if (contactId._id) {
            return String(contactId._id);
        }
        // Last resort - return empty string
        return '';
    }
    
    // For other types, convert to string
    const str = String(contactId);
    return str !== '[object Object]' ? str : '';
};

const AudienceBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditing = !!id;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [audience, setAudience] = useState({
        name: '',
        description: '',
        type: 'static',
        contacts: [],
        filters: {},
        segmentQuery: ''
    });
    
    const [availableContacts, setAvailableContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // CSV Import state
    const [csvImportStep, setCsvImportStep] = useState(null); // null, 'upload', 'mapping', 'preview', 'importing'
    const [csvFile, setCsvFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [mappedData, setMappedData] = useState([]);
    const [importResults, setImportResults] = useState(null);
    
    // Manual contact input state
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualInput, setManualInput] = useState('');
    const [parsedManualContacts, setParsedManualContacts] = useState([]);
    const [manualInputProcessing, setManualInputProcessing] = useState(false);

    useEffect(() => {
        if (isEditing) {
            loadAudience();
        }
        loadContacts();
    }, [id]);

    const loadAudience = async () => {
        try {
            setLoading(true);
            const response = await audiencesAPI.getById(id);
            if (response.data.success) {
                const audienceData = response.data.data;
                
                // Debug: log raw contacts from backend
                console.log('Raw audience data from backend:', audienceData);
                console.log('Raw contacts from backend:', audienceData.contacts);
                console.log('Contacts type:', typeof audienceData.contacts);
                console.log('Contacts is array:', Array.isArray(audienceData.contacts));
                if (audienceData.contacts && audienceData.contacts.length > 0) {
                    console.log('First contact raw:', audienceData.contacts[0]);
                    console.log('First contact type:', typeof audienceData.contacts[0]);
                }
                
                // Normalize contacts: if any are in old format (string IDs), fetch their details
                if (audienceData.contacts && audienceData.contacts.length > 0) {
                    const normalizedContacts = [];
                    
                    for (const contact of audienceData.contacts) {
                        console.log('Processing contact:', contact, 'Type:', typeof contact);
                        
                        
                        // New format: has contactType and either contactId or _id (Mongoose adds _id to subdocuments)
                        if (typeof contact === 'object' && contact !== null && (contact.contactId || contact._id)) {
                            // Use contactId if available, otherwise use _id (might be the actual contact ID)
                            // Convert to string immediately to handle ObjectId objects
                            let contactId = contact.contactId || contact._id;
                            // Properly convert to string - handle ObjectId objects
                            const contactIdStr = safeContactIdToString(contactId);
                            console.log('Contact is in new format, contactId:', contactIdStr, 'from contactId field:', contact.contactId, 'from _id field:', contact._id);
                            
                            // If we have email or name, use them directly (already normalized)
                            if (contact.email || contact.name) {
                                normalizedContacts.push({
                                    contactType: contact.contactType || 'client',
                                    contactId: contactIdStr,
                                    email: contact.email || '',
                                    name: contact.name || contact.email || 'Unnamed'
                                });
                            } else {
                                // Has contactType and contactId/_id but missing email/name - fetch details
                                console.log('Contact has contactId/_id but missing email/name, fetching details. Using contactId:', contactIdStr, 'contactType:', contact.contactType);
                                let contactFound = false;
                                
                                // Try client first (only if contactIdStr is valid)
                                if (!contactIdStr || contactIdStr === '[object Object]' || contactIdStr.length === 0) {
                                    console.warn('Invalid contactIdStr, skipping client lookup:', contactIdStr);
                                } else {
                                    try {
                                        const clientResponse = await clientAPI.getById(contactIdStr);
                                        if (clientResponse?.data?.success && clientResponse.data.data) {
                                            const client = clientResponse.data.data;
                                            normalizedContacts.push({
                                                contactType: contact.contactType || 'client',
                                                contactId: client._id || client.id,
                                                email: client.email || '',
                                                name: client.name || client.email || 'Unnamed'
                                            });
                                            contactFound = true;
                                        }
                                    } catch (clientError) {
                                        if (clientError.response?.status !== 404) {
                                            console.error(`Error fetching client ${contactIdStr}:`, clientError);
                                        }
                                    }
                                }
                                
                                // If not found as client, try lead (only if contactIdStr is valid)
                                if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                    try {
                                        const leadResponse = await leadAPI.getById(contactIdStr);
                                        if (leadResponse?.data?.success && leadResponse.data.data) {
                                            const lead = leadResponse.data.data;
                                            normalizedContacts.push({
                                                contactType: contact.contactType || 'lead',
                                                contactId: lead._id || lead.id,
                                                email: lead.email || '',
                                                name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email || 'Unnamed'
                                            });
                                            contactFound = true;
                                        }
                                    } catch (leadError) {
                                        if (leadError.response?.status !== 404) {
                                            console.error(`Error fetching lead ${contactIdStr}:`, leadError);
                                        }
                                    }
                                }
                                
                                // If not found as client or lead, try account contact
                                if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                    try {
                                        const accountResponse = await accountAPI.getById(contactIdStr);
                                        if (accountResponse?.data?.success && accountResponse.data.data) {
                                            const account = accountResponse.data.data;
                                            if (account.primaryContact?.email) {
                                                normalizedContacts.push({
                                                    contactType: 'accountContact',
                                                    contactId: account._id || account.id,
                                                    email: account.primaryContact.email || '',
                                                    name: account.primaryContact.name || account.name || 'Unnamed'
                                                });
                                                contactFound = true;
                                            }
                                        }
                                    } catch (accountError) {
                                        if (accountError.response?.status !== 404) {
                                            console.error(`Error fetching account ${contactIdStr}:`, accountError);
                                        }
                                    }
                                }
                                
                                // If not found as client, lead, or account, try contact
                                if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                    try {
                                        const contactResponse = await contactAPI.getById(contactIdStr);
                                        if (contactResponse?.data?.success && contactResponse.data.data) {
                                            const contact = contactResponse.data.data;
                                            normalizedContacts.push({
                                                contactType: 'contact',
                                                contactId: contact._id || contact.id,
                                                email: contact.email || '',
                                                name: contact.name || contact.fullName || contact.email || 'Unnamed'
                                            });
                                            contactFound = true;
                                        }
                                    } catch (contactError) {
                                        if (contactError.response?.status !== 404) {
                                            console.error(`Error fetching contact ${contactIdStr}:`, contactError);
                                        }
                                    }
                                }
                                
                                // If still not found, show it with limited info (contact might have been deleted)
                                if (!contactFound) {
                                    // Ensure contactIdStr is a string for display
                                    const contactIdForDisplay = String(contactIdStr || '');
                                    const shortId = contactIdForDisplay.length > 12 ? contactIdForDisplay.substring(0, 12) : contactIdForDisplay;
                                    console.warn(`Contact ${contactIdStr} not found in clients, leads, or accounts - showing with limited info`);
                                    // Use stored email/name if available, otherwise show unknown
                                    const displayName = contact.name || contact.email || `Unknown Contact (${shortId}...)`;
                                    normalizedContacts.push({
                                        contactType: contact.contactType || 'client',
                                        contactId: contactIdStr,
                                        email: contact.email || '',
                                        name: displayName
                                    });
                                }
                            }
                        } else {
                            console.log('Contact is in old format (string ID), attempting to fetch:', contact);
                            // Old format: just an ID string - fetch details
                            let contactId = typeof contact === 'string' ? contact : (contact._id || contact);
                            // Convert to string (handle ObjectId objects)
                            const contactIdStr = safeContactIdToString(contactId);
                            
                            let contactFound = false;
                            
                            // Try client first (only if contactIdStr is valid)
                            if (!contactIdStr || contactIdStr === '[object Object]' || contactIdStr.length === 0) {
                                console.warn('Invalid contactIdStr, skipping client lookup:', contactIdStr);
                            } else {
                                try {
                                    const clientResponse = await clientAPI.getById(contactIdStr);
                                    if (clientResponse?.data?.success && clientResponse.data.data) {
                                        const client = clientResponse.data.data;
                                        normalizedContacts.push({
                                            contactType: 'client',
                                            contactId: client._id || client.id,
                                            email: client.email || '',
                                            name: client.name || client.email || 'Unnamed'
                                        });
                                        contactFound = true;
                                    }
                                } catch (clientError) {
                                    // 404 is expected if it's not a client, continue to try lead
                                    if (clientError.response?.status !== 404) {
                                        console.error(`Error fetching client ${contactIdStr}:`, clientError);
                                    }
                                }
                            }
                            
                            // If not found as client, try lead (only if contactIdStr is valid)
                            if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                try {
                                    const leadResponse = await leadAPI.getById(contactIdStr);
                                    if (leadResponse?.data?.success && leadResponse.data.data) {
                                        const lead = leadResponse.data.data;
                                        normalizedContacts.push({
                                            contactType: 'lead',
                                            contactId: lead._id || lead.id,
                                            email: lead.email || '',
                                            name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.email || 'Unnamed'
                                        });
                                        contactFound = true;
                                    }
                                } catch (leadError) {
                                    // 404 is expected if it's not a lead, continue
                                    if (leadError.response?.status !== 404) {
                                        console.error(`Error fetching lead ${contactIdStr}:`, leadError);
                                    }
                                }
                            }
                            
                            // If not found as client or lead, try account contact
                            if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                try {
                                    const accountResponse = await accountAPI.getById(contactIdStr);
                                    if (accountResponse?.data?.success && accountResponse.data.data) {
                                        const account = accountResponse.data.data;
                                        if (account.primaryContact?.email) {
                                            normalizedContacts.push({
                                                contactType: 'accountContact',
                                                contactId: account._id || account.id,
                                                email: account.primaryContact.email || '',
                                                name: account.primaryContact.name || account.name || 'Unnamed'
                                            });
                                            contactFound = true;
                                        }
                                    }
                                } catch (accountError) {
                                    if (accountError.response?.status !== 404) {
                                        console.error(`Error fetching account ${contactIdStr}:`, accountError);
                                    }
                                }
                            }
                            
                            // If not found as client, lead, or account, try contact
                            if (!contactFound && contactIdStr && contactIdStr !== '[object Object]' && contactIdStr.length > 0) {
                                try {
                                    const contactResponse = await contactAPI.getById(contactIdStr);
                                    if (contactResponse?.data?.success && contactResponse.data.data) {
                                        const contact = contactResponse.data.data;
                                        normalizedContacts.push({
                                            contactType: 'contact',
                                            contactId: contact._id || contact.id,
                                            email: contact.email || '',
                                            name: contact.name || contact.fullName || contact.email || 'Unnamed'
                                        });
                                        contactFound = true;
                                    }
                                } catch (contactError) {
                                    if (contactError.response?.status !== 404) {
                                        console.error(`Error fetching contact ${contactIdStr}:`, contactError);
                                    }
                                }
                            }
                            
                            // If still not found, show it with limited info (contact might have been deleted)
                            if (!contactFound) {
                                // Ensure contactIdStr is a string for display
                                const contactIdForDisplay = String(contactIdStr || '');
                                const shortId = contactIdForDisplay.length > 12 ? contactIdForDisplay.substring(0, 12) : contactIdForDisplay;
                                console.warn(`Contact ${contactIdStr} not found in clients, leads, or accounts - showing with limited info`);
                                normalizedContacts.push({
                                    contactType: 'client', // Default since we don't know
                                    contactId: contactIdStr,
                                    email: '',
                                    name: `Unknown Contact (${shortId}...)`
                                });
                            }
                        }
                    }
                    
                    audienceData.contacts = normalizedContacts;
                }
                
                // Debug: log the contacts structure
                console.log('Loaded audience contacts:', audienceData.contacts);
                
                setAudience(audienceData);
            }
        } catch (error) {
            console.error('Failed to load audience:', error);
            toast.error('Failed to load audience');
        } finally {
            setLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            setContactsLoading(true);
            // Load clients and contacts
            const [clientsResponse, contactsResponse] = await Promise.all([
                clientAPI.getAll({ limit: 1000 }),
                contactAPI.getAll({ limit: 1000 })
            ]);
            
            const allContacts = [];
            
            if (clientsResponse.data.success) {
                allContacts.push(...(clientsResponse.data.data || []));
            }
            
            if (contactsResponse.data.success) {
                // Add contacts from Contact collection
                const contacts = contactsResponse.data.data || [];
                allContacts.push(...contacts);
            }
            
            setAvailableContacts(allContacts);
        } catch (error) {
            console.error('Failed to load contacts:', error);
        } finally {
            setContactsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!audience.name) {
            toast.error('Audience name is required');
            return;
        }

        if (audience.type === 'static' && audience.contacts.length === 0) {
            toast.error('Please add at least one contact to the static list');
            return;
        }

        try {
            setSaving(true);
            let response;
            if (isEditing) {
                response = await audiencesAPI.update(id, audience);
            } else {
                response = await audiencesAPI.create(audience);
            }

            if (response.data.success) {
                toast.success(`Audience ${isEditing ? 'updated' : 'created'} successfully`);
                // Return to campaign builder if we came from there, otherwise go to audiences tab
                const returnTo = location.state?.returnTo || '/campaigns?tab=audiences';
                navigate(returnTo);
            }
        } catch (error) {
            console.error('Failed to save audience:', error);
            toast.error('Failed to save audience');
        } finally {
            setSaving(false);
        }
    };

    const handleAddContact = (contactId) => {
        if (!audience.contacts.includes(contactId)) {
            setAudience(prev => ({
                ...prev,
                contacts: [...prev.contacts, contactId]
            }));
        }
    };

    const handleRemoveContact = (contactId, contactType = null, email = null) => {
        setAudience(prev => ({
            ...prev,
            contacts: prev.contacts.filter(contact => {
                // Handle new format (object with contactId and contactType)
                if (typeof contact === 'object' && contact.contactId) {
                    if (contactType) {
                        return !(String(contact.contactId) === String(contactId) && contact.contactType === contactType);
                    }
                    return String(contact.contactId) !== String(contactId);
                }
                // Handle old format (just ID string)
                return String(contact) !== String(contactId);
            })
        }));
    };

    const filteredContacts = availableContacts.filter(contact => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            contact.name?.toLowerCase().includes(query) ||
            contact.email?.toLowerCase().includes(query) ||
            contact.company?.toLowerCase().includes(query)
        );
    });

    // Get selected contacts from audience.contacts array
    // After loadAudience, all contacts should be in new format (objects with contactType, contactId, email, name)
    // Debug: log the audience contacts structure
    if (audience.contacts && audience.contacts.length > 0) {
        console.log('Audience contacts for selectedContacts:', audience.contacts);
        console.log('First contact structure:', audience.contacts[0]);
        console.log('First contact contactId:', audience.contacts[0]?.contactId);
        console.log('First contact contactId type:', typeof audience.contacts[0]?.contactId);
    }
    
    const selectedContacts = (audience.contacts || [])
        .filter(contact => {
            // Handle both new format (object with contactId) and old format (string ID)
            if (typeof contact === 'object' && contact !== null) {
                // New format: has contactId (can be string or ObjectId)
                if (contact.contactId) {
                    return true;
                }
                // Might be old format with _id
                if (contact._id) {
                    return true;
                }
            }
            // Old format: just a string ID
            if (typeof contact === 'string') {
                return true;
            }
            return false;
        })
        .map(contact => {
            // New format: object with contactId
            if (typeof contact === 'object' && contact !== null && contact.contactId) {
                return {
                    _id: String(contact.contactId), // Ensure it's a string
                    email: contact.email || '',
                    name: contact.name || contact.email || 'Unnamed',
                    contactType: contact.contactType || 'client'
                };
            }
            // Old format: object with _id (from availableContacts)
            if (typeof contact === 'object' && contact !== null && contact._id) {
                return {
                    _id: String(contact._id),
                    email: contact.email || '',
                    name: contact.name || contact.email || 'Unnamed',
                    contactType: 'client'
                };
            }
            // Old format: just a string ID - try to find in availableContacts
            if (typeof contact === 'string') {
                const found = availableContacts.find(c => String(c._id) === contact);
                if (found) {
                    return {
                        _id: String(found._id),
                        email: found.email || '',
                        name: found.name || found.email || 'Unnamed',
                        contactType: 'client'
                    };
                }
                // Return placeholder if not found
                return {
                    _id: contact,
                    email: '',
                    name: 'Unknown Contact',
                    contactType: 'client'
                };
            }
            return null;
        })
        .filter(Boolean);
    
    // Parse manual contact input
    const parseManualContacts = (input) => {
        if (!input || !input.trim()) return [];
        
        const lines = input.split('\n').filter(line => line.trim());
        const contacts = [];
        
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            
            // Check if this line contains multiple emails (comma-separated emails)
            // Pattern: email1@example.com, email2@example.com, email3@example.com
            const emailPattern = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
            const emails = trimmed.match(emailPattern);
            
            if (emails && emails.length > 1) {
                // Multiple emails on one line - treat each as a separate contact
                for (const email of emails) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (emailRegex.test(email)) {
                        contacts.push({
                            email: email.toLowerCase(),
                            name: '',
                            firstName: '',
                            lastName: '',
                            phone: '',
                            company: ''
                        });
                    }
                }
                continue;
            }
            
            // Single contact per line - parse with different formats:
            // 1. Just email: email@example.com
            // 2. Tab-separated: email@example.com\tName\tPhone\tCompany
            // 3. Comma-separated: email@example.com,Name,Phone,Company
            // 4. Space-separated (if no tabs/commas): email@example.com Name Phone Company
            
            let parts = [];
            if (trimmed.includes('\t')) {
                parts = trimmed.split('\t').map(p => p.trim());
            } else if (trimmed.includes(',')) {
                parts = trimmed.split(',').map(p => p.trim());
            } else {
                // Space-separated, but email must be first
                const spaceParts = trimmed.split(/\s+/);
                parts = [spaceParts[0]]; // Email is first
                if (spaceParts.length > 1) {
                    // Try to combine remaining parts as name
                    parts.push(spaceParts.slice(1).join(' '));
                }
            }
            
            const email = parts[0]?.trim();
            if (!email || !email.includes('@')) {
                continue; // Skip invalid emails
            }
            
            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                continue; // Skip invalid email formats
            }
            
            const contact = {
                email: email.toLowerCase(),
                name: parts[1] || '',
                firstName: parts[1]?.split(' ')[0] || '',
                lastName: parts[1]?.split(' ').slice(1).join(' ') || '',
                phone: parts[2] || '',
                company: parts[3] || ''
            };
            
            contacts.push(contact);
        }
        
        return contacts;
    };
    
    // Handle manual contact input change
    const handleManualInputChange = (value) => {
        setManualInput(value);
        const parsed = parseManualContacts(value);
        setParsedManualContacts(parsed);
    };
    
    // Add manually entered contacts
    const handleAddManualContacts = async () => {
        if (parsedManualContacts.length === 0) {
            toast.error('No valid contacts found. Please enter at least one email address.');
            return;
        }
        
        try {
            setManualInputProcessing(true);
            let added = 0;
            let skipped = 0;
            let errors = [];
            
            for (const contact of parsedManualContacts) {
                try {
                    // Search across clients, leads, and account contacts
                    let foundContact = null;
                    let contactType = null;
                    
                    // First check available contacts (already loaded)
                    const existingContact = availableContacts.find(c => 
                        c.email?.toLowerCase() === contact.email.toLowerCase()
                    );
                    
                    if (existingContact) {
                        foundContact = existingContact;
                        contactType = 'client'; // availableContacts are clients
                    } else {
                        // Search clients
                        try {
                            const clientResponse = await clientAPI.getAll({ 
                                search: contact.email,
                                limit: 10 
                            });
                            
                            if (clientResponse.data.success) {
                                const found = clientResponse.data.data.find(c => 
                                    c.email?.toLowerCase() === contact.email.toLowerCase()
                                );
                                if (found) {
                                    foundContact = found;
                                    contactType = 'client';
                                }
                            }
                        } catch (err) {
                            console.error('Error searching clients:', err);
                        }
                        
                        // If not found in clients, search leads
                        if (!foundContact) {
                            try {
                                const leadResponse = await leadAPI.getAll({ 
                                    search: contact.email,
                                    limit: 10 
                                });
                                
                                if (leadResponse.data.success) {
                                    const found = leadResponse.data.data.find(l => 
                                        l.email?.toLowerCase() === contact.email.toLowerCase()
                                    );
                                    if (found) {
                                        foundContact = found;
                                        contactType = 'lead';
                                    }
                                }
                            } catch (err) {
                                console.error('Error searching leads:', err);
                            }
                        }
                        
                        // If still not found, search account primary contacts
                        if (!foundContact) {
                            try {
                                const accountResponse = await accountAPI.getAll({ 
                                    limit: 1000 
                                });
                                
                                if (accountResponse.data.success) {
                                    const found = accountResponse.data.data.find(a => 
                                        a.primaryContact?.email?.toLowerCase() === contact.email.toLowerCase()
                                    );
                                    if (found) {
                                        foundContact = {
                                            _id: found._id,
                                            email: found.primaryContact.email,
                                            name: found.primaryContact.name || found.name
                                        };
                                        contactType = 'accountContact';
                                    }
                                }
                            } catch (err) {
                                console.error('Error searching accounts:', err);
                            }
                        }
                    }
                    
                    // Add to audience if contact found, or add as unresolved if not found
                    if (foundContact && contactType) {
                        // Check if already in audience
                        const alreadyAdded = audience.contacts.some(c => 
                            (typeof c === 'object' && c.contactId === foundContact._id && c.contactType === contactType) ||
                            (typeof c === 'string' && c === foundContact._id && contactType === 'client') // Backward compatibility
                        );
                        
                        if (!alreadyAdded) {
                            // Add as new format: { contactType, contactId, email, name }
                            const newContact = {
                                contactType,
                                contactId: foundContact._id,
                                email: foundContact.email || contact.email,
                                name: foundContact.name || foundContact.firstName || contact.name || ''
                            };
                            
                            setAudience(prev => ({
                                ...prev,
                                contacts: [...prev.contacts, newContact]
                            }));
                            added++;
                        } else {
                            skipped++;
                        }
                    } else {
                        // Contact not found - create in Contact collection
                        // Check if already in audience by email
                        const alreadyAdded = audience.contacts.some(c => 
                            typeof c === 'object' && 
                            c.email?.toLowerCase() === contact.email.toLowerCase()
                        );
                        
                        if (!alreadyAdded) {
                            try {
                                // Create contact in Contact collection
                                const nameParts = (contact.name || '').trim().split(' ');
                                const firstName = nameParts[0] || '';
                                const lastName = nameParts.slice(1).join(' ') || '';
                                
                                const contactData = {
                                    email: contact.email,
                                    firstName,
                                    lastName,
                                    name: contact.name || '',
                                    phone: contact.phone || '',
                                    company: contact.company || '',
                                    source: 'manual'
                                };
                                
                                const createResponse = await contactAPI.create(contactData);
                                
                                if (createResponse.data.success) {
                                    const createdContact = createResponse.data.data;
                                    
                                    // Add to audience
                                    const newContact = {
                                        contactType: 'contact',
                                        contactId: createdContact._id,
                                        email: createdContact.email,
                                        name: createdContact.name || createdContact.fullName || createdContact.email
                                    };
                                    
                                    setAudience(prev => ({
                                        ...prev,
                                        contacts: [...prev.contacts, newContact]
                                    }));
                                    added++;
                                    
                                    // Reload contacts so it appears immediately in select contacts section
                                    loadContacts();
                                } else {
                                    throw new Error(createResponse.data.message || 'Failed to create contact');
                                }
                            } catch (createError) {
                                console.error('Error creating contact:', createError);
                                skipped++;
                                errors.push({
                                    email: contact.email,
                                    error: createError.response?.data?.message || createError.message || 'Failed to create contact'
                                });
                            }
                        } else {
                            skipped++;
                        }
                    }
                } catch (error) {
                    console.error('Error processing contact:', contact.email, error);
                    errors.push(contact.email);
                }
            }
            
            // Show results
            if (added > 0 && skipped === 0) {
                toast.success(`Successfully added ${added} contact(s) to audience`);
            } else if (added > 0 && skipped > 0) {
                toast.warning(`Added ${added} contact(s), ${skipped} skipped (already in audience)`);
            } else if (skipped > 0) {
                toast.warning(`${skipped} contact(s) skipped (already in audience)`);
            }
            
            // Clear input
            setManualInput('');
            setParsedManualContacts([]);
            setShowManualInput(false);
            
            // Reload contacts to get updated list
            loadContacts();
        } catch (error) {
            console.error('Error adding manual contacts:', error);
            toast.error('Failed to add contacts');
        } finally {
            setManualInputProcessing(false);
        }
    };

    // CSV Parser function
    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length === 0) return { headers: [], data: [] };
        
        // Detect delimiter (comma or semicolon)
        const firstLine = lines[0];
        const delimiter = firstLine.includes(',') ? ',' : (firstLine.includes(';') ? ';' : ',');
        
        // Parse headers
        const headers = firstLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
        
        // Parse data rows
        const data = lines.slice(1).map(line => {
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === delimiter && !inQuotes) {
                    values.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            values.push(current.trim());
            
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || '';
            });
            return row;
        }).filter(row => Object.values(row).some(val => val.trim() !== ''));
        
        return { headers, data };
    };

    // Handle CSV file upload
    const handleCsvFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }
        
        setCsvFile(file);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const { headers, data } = parseCSV(text);
                
                if (headers.length === 0 || data.length === 0) {
                    toast.error('CSV file is empty or invalid');
                    return;
                }
                
                setCsvHeaders(headers);
                setCsvData(data);
                
                // Auto-map common column names
                const autoMapping = {};
                const fieldMappings = {
                    email: ['email', 'e-mail', 'e_mail', 'email address', 'emailaddress'],
                    name: ['name', 'full name', 'fullname', 'full_name', 'contact name', 'contactname'],
                    firstName: ['first name', 'firstname', 'first_name', 'fname', 'given name'],
                    lastName: ['last name', 'lastname', 'last_name', 'lname', 'surname', 'family name'],
                    phone: ['phone', 'telephone', 'tel', 'phone number', 'phonenumber', 'mobile', 'cell'],
                    company: ['company', 'organization', 'org', 'company name', 'companyname'],
                    country: ['country', 'nationality', 'country code', 'countrycode'],
                    tags: ['tags', 'tag', 'labels', 'label']
                };
                
                headers.forEach(header => {
                    const headerLower = header.toLowerCase().trim();
                    for (const [field, variations] of Object.entries(fieldMappings)) {
                        if (variations.some(v => headerLower.includes(v))) {
                            autoMapping[header] = field;
                            break;
                        }
                    }
                });
                
                setColumnMapping(autoMapping);
                setCsvImportStep('mapping');
            } catch (error) {
                console.error('Error parsing CSV:', error);
                toast.error('Failed to parse CSV file');
            }
        };
        
        reader.readAsText(file);
    };

    // Map columns and preview data
    const handleColumnMapping = () => {
        if (!columnMapping.email) {
            toast.error('Please map at least the Email column');
            return;
        }
        
        const mapped = csvData.map(row => {
            const mappedRow = {};
            Object.entries(columnMapping).forEach(([csvColumn, field]) => {
                if (field && row[csvColumn]) {
                    mappedRow[field] = row[csvColumn].trim();
                }
            });
            return mappedRow;
        }).filter(row => row.email); // Only include rows with email
        
        setMappedData(mapped);
        setCsvImportStep('preview');
    };

    // Import contacts from CSV
    const handleImportContacts = async () => {
        if (mappedData.length === 0) {
            toast.error('No valid contacts to import');
            return;
        }
        
        try {
            setCsvImportStep('importing');
            setImportResults({ added: 0, skipped: 0, errors: [] });
            
            const results = { added: 0, skipped: 0, errors: [] };
            
            for (const contact of mappedData) {
                try {
                    // Search across clients, leads, and account contacts
                    let foundContact = null;
                    let contactType = null;
                    
                    // First check available contacts (already loaded)
                    const existingContact = availableContacts.find(c => 
                        c.email?.toLowerCase() === contact.email.toLowerCase()
                    );
                    
                    if (existingContact) {
                        foundContact = existingContact;
                        contactType = 'client'; // availableContacts are clients
                    } else {
                        // Search clients
                        try {
                            const clientResponse = await clientAPI.getAll({ 
                                search: contact.email,
                                limit: 10 
                            });
                            
                            if (clientResponse.data.success) {
                                const found = clientResponse.data.data.find(c => 
                                    c.email?.toLowerCase() === contact.email.toLowerCase()
                                );
                                if (found) {
                                    foundContact = found;
                                    contactType = 'client';
                                }
                            }
                        } catch (err) {
                            console.error('Error searching clients:', err);
                        }
                        
                        // If not found in clients, search leads
                        if (!foundContact) {
                            try {
                                const leadResponse = await leadAPI.getAll({ 
                                    search: contact.email,
                                    limit: 10 
                                });
                                
                                if (leadResponse.data.success) {
                                    const found = leadResponse.data.data.find(l => 
                                        l.email?.toLowerCase() === contact.email.toLowerCase()
                                    );
                                    if (found) {
                                        foundContact = found;
                                        contactType = 'lead';
                                    }
                                }
                            } catch (err) {
                                console.error('Error searching leads:', err);
                            }
                        }
                        
                        // If still not found, search account primary contacts
                        if (!foundContact) {
                            try {
                                const accountResponse = await accountAPI.getAll({ 
                                    limit: 1000 
                                });
                                
                                if (accountResponse.data.success) {
                                    const found = accountResponse.data.data.find(a => 
                                        a.primaryContact?.email?.toLowerCase() === contact.email.toLowerCase()
                                    );
                                    if (found) {
                                        foundContact = {
                                            _id: found._id,
                                            email: found.primaryContact.email,
                                            name: found.primaryContact.name || found.name
                                        };
                                        contactType = 'accountContact';
                                    }
                                }
                            } catch (err) {
                                console.error('Error searching accounts:', err);
                            }
                        }
                    }
                    
                    // Add to audience if contact found, or add as unresolved if not found
                    if (foundContact && contactType) {
                        // Check if already in audience
                        const alreadyAdded = audience.contacts.some(c => 
                            (typeof c === 'object' && c.contactId === foundContact._id && c.contactType === contactType) ||
                            (typeof c === 'string' && c === foundContact._id && contactType === 'client') // Backward compatibility
                        );
                        
                        if (!alreadyAdded) {
                            // Add as new format: { contactType, contactId, email, name }
                            const newContact = {
                                contactType,
                                contactId: foundContact._id,
                                email: foundContact.email || contact.email,
                                name: foundContact.name || foundContact.firstName || contact.name || ''
                            };
                            
                            setAudience(prev => ({
                                ...prev,
                                contacts: [...prev.contacts, newContact]
                            }));
                            results.added++;
                        } else {
                            results.skipped++;
                        }
                    } else {
                        // Contact not found - create in Contact collection
                        // Check if already in audience by email
                        const alreadyAdded = audience.contacts.some(c => 
                            typeof c === 'object' && 
                            c.email?.toLowerCase() === contact.email.toLowerCase()
                        );
                        
                        if (!alreadyAdded) {
                            try {
                                // Create contact in Contact collection
                                const nameParts = (contact.name || contact.firstName || '').trim().split(' ');
                                const firstName = nameParts[0] || contact.firstName || '';
                                const lastName = nameParts.slice(1).join(' ') || contact.lastName || '';
                                
                                const contactData = {
                                    email: contact.email,
                                    firstName,
                                    lastName,
                                    name: contact.name || contact.firstName || '',
                                    phone: contact.phone || '',
                                    company: contact.company || '',
                                    source: 'csv_import'
                                };
                                
                                const createResponse = await contactAPI.create(contactData);
                                
                                if (createResponse.data.success) {
                                    const createdContact = createResponse.data.data;
                                    
                                    // Add to audience
                                    const newContact = {
                                        contactType: 'contact',
                                        contactId: createdContact._id,
                                        email: createdContact.email,
                                        name: createdContact.name || createdContact.fullName || createdContact.email
                                    };
                                    
                                    setAudience(prev => ({
                                        ...prev,
                                        contacts: [...prev.contacts, newContact]
                                    }));
                                    results.added++;
                                } else {
                                    throw new Error(createResponse.data.message || 'Failed to create contact');
                                }
                            } catch (createError) {
                                console.error('Error creating contact:', createError);
                                results.skipped++;
                                results.errors.push({
                                    email: contact.email,
                                    error: createError.response?.data?.message || createError.message || 'Failed to create contact'
                                });
                            }
                        } else {
                            results.skipped++;
                        }
                    }
                } catch (error) {
                    results.errors.push({
                        email: contact.email,
                        error: error.response?.data?.message || error.message || 'Unknown error'
                    });
                }
            }
            
            // Save audience with updated contacts
            if (results.added > 0) {
                try {
                    const updatedAudience = {
                        ...audience,
                        contacts: [...new Set(audience.contacts)] // Remove duplicates
                    };
                    
                    if (isEditing) {
                        await audiencesAPI.update(id, updatedAudience);
                    } else {
                        // If creating new, we'll save when user clicks Save
                        setAudience(updatedAudience);
                    }
                } catch (error) {
                    console.error('Failed to save audience:', error);
                    toast.error('Contacts added but failed to save audience. Please save manually.');
                }
            }
            
            setImportResults(results);
            
            if (results.added > 0) {
                toast.success(`Added ${results.added} contact(s) to audience`);
                // Reload contacts to show updated list
                loadContacts();
            }
            
            if (results.skipped > 0 && results.added === 0) {
                toast.warning(`${results.skipped} contact(s) skipped (already in audience)`);
            } else if (results.skipped > 0) {
                toast.warning(`${results.added} added, ${results.skipped} skipped (already in audience)`);
            }
            
            if (results.errors.length > 0 && results.added === 0) {
                toast.error(`${results.errors.length} contact(s) failed to add`);
            }
            
            // Close import after a delay
            setTimeout(() => {
                setCsvImportStep(null);
                setCsvFile(null);
                setCsvData([]);
                setCsvHeaders([]);
                setColumnMapping({});
                setMappedData([]);
                setImportResults(null);
            }, 3000);
            
        } catch (error) {
            console.error('Error importing contacts:', error);
            toast.error('Failed to import contacts');
            setCsvImportStep('preview');
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="text-center py-12">Loading audience...</div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">
                        {isEditing ? 'Edit Audience' : 'Create Audience'}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {isEditing ? 'Update your audience settings' : 'Create a static list or dynamic segment'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            // Return to campaign builder if we came from there, otherwise go to audiences tab
                            const returnTo = location.state?.returnTo || '/campaigns?tab=audiences';
                            navigate(returnTo);
                        }}
                        className="px-4 py-2 border border-input rounded-lg hover:bg-muted"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Audience'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic Info */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Basic Information</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Audience Name *
                                </label>
                                <input
                                    type="text"
                                    value={audience.name}
                                    onChange={(e) => setAudience(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="e.g., VIP Clients, Spanish Residents"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={audience.description}
                                    onChange={(e) => setAudience(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    rows={3}
                                    placeholder="Describe this audience..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Audience Type *
                                </label>
                                <select
                                    value={audience.type}
                                    onChange={(e) => setAudience(prev => ({ ...prev, type: e.target.value, contacts: e.target.value === 'segment' ? [] : prev.contacts }))}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    disabled={isEditing}
                                >
                                    <option value="static">Static List (Manual selection)</option>
                                    <option value="segment">Dynamic Segment (Filter-based)</option>
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {audience.type === 'static'
                                        ? 'Manually select contacts for this list'
                                        : 'Automatically include contacts based on filters'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contacts/Segment Configuration */}
                    {audience.type === 'static' ? (
                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-foreground">Select Contacts</h2>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowManualInput(!showManualInput)}
                                        className="px-3 py-1.5 border border-input rounded-md hover:bg-muted flex items-center gap-2 text-sm"
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                        Add Manually
                                    </button>
                                    <button
                                        onClick={() => setCsvImportStep('upload')}
                                        className="px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary/90 flex items-center gap-2 text-sm"
                                    >
                                        <ArrowUpTrayIcon className="h-4 w-4" />
                                        Import from CSV
                                    </button>
                                </div>
                            </div>
                            
                            {/* Manual Contact Input */}
                            {showManualInput && (
                                <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-semibold text-foreground">Add Contacts Manually</h3>
                                        <button
                                            onClick={() => {
                                                setShowManualInput(false);
                                                setManualInput('');
                                                setParsedManualContacts([]);
                                            }}
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Enter one contact per line. Email is required. Format: <code className="bg-background px-1 py-0.5 rounded">email@example.com</code> or <code className="bg-background px-1 py-0.5 rounded">email@example.com Name Phone Company</code> (tab or comma separated)
                                    </p>
                                    <p className="text-xs text-muted-foreground italic mb-3">
                                        Note: Contacts will be automatically created as leads if they don't already exist in the system.
                                    </p>
                                    <textarea
                                        value={manualInput}
                                        onChange={(e) => handleManualInputChange(e.target.value)}
                                        placeholder="email@example.com&#10;email2@example.com John Doe +1234567890 Company Name&#10;email3@example.com, Jane Smith, +9876543210, Another Company"
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground font-mono text-sm min-h-[120px] mb-3"
                                        rows={5}
                                    />
                                    {parsedManualContacts.length > 0 && (
                                        <div className="mb-3">
                                            <p className="text-xs text-muted-foreground mb-2">
                                                Found {parsedManualContacts.length} valid contact(s):
                                            </p>
                                            <div className="max-h-32 overflow-y-auto space-y-1">
                                                {parsedManualContacts.map((contact, idx) => (
                                                    <div key={idx} className="text-xs bg-background p-2 rounded border border-border">
                                                        <span className="font-medium">{contact.email}</span>
                                                        {contact.name && <span className="text-muted-foreground"> • {contact.name}</span>}
                                                        {contact.phone && <span className="text-muted-foreground"> • {contact.phone}</span>}
                                                        {contact.company && <span className="text-muted-foreground"> • {contact.company}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleAddManualContacts}
                                        disabled={parsedManualContacts.length === 0 || manualInputProcessing}
                                        className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {manualInputProcessing ? (
                                            <>
                                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                                Adding Contacts...
                                            </>
                                        ) : (
                                            <>
                                                <PlusIcon className="h-4 w-4" />
                                                Add {parsedManualContacts.length > 0 ? `${parsedManualContacts.length} Contact(s)` : 'Contacts'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                            
                            {/* Selected Contacts */}
                            {selectedContacts.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-foreground mb-2">
                                        Selected Contacts ({selectedContacts.length})
                                    </h3>
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {selectedContacts.map(contact => (
                                            <div
                                                key={`${contact.contactType || 'client'}-${contact._id}`}
                                                className="flex items-center justify-between p-2 bg-muted rounded-md"
                                            >
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-foreground">{contact.name || 'Unnamed'}</span>
                                                        {contact.contactType && (
                                                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                contact.contactType === 'client' ? 'bg-blue-100 text-blue-700' :
                                                                contact.contactType === 'lead' ? 'bg-green-100 text-green-700' :
                                                                contact.contactType === 'accountContact' ? 'bg-purple-100 text-purple-700' :
                                                                contact.contactType === 'contact' ? 'bg-gray-100 text-gray-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {contact.contactType === 'client' ? 'Client' :
                                                                 contact.contactType === 'lead' ? 'Lead' :
                                                                 contact.contactType === 'accountContact' ? 'Account' :
                                                                 contact.contactType === 'contact' ? 'Contact' :
                                                                 'Unknown'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">{contact.email}</div>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveContact(contact.contactId || contact._id, contact.contactType, contact.email)}
                                                    className="text-red-600 hover:text-red-800 ml-2"
                                                >
                                                    <XMarkIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Search and Add */}
                            <div>
                                <h3 className="text-sm font-medium text-foreground mb-2">Add Contacts</h3>
                                <div className="mb-3">
                                    <div className="relative">
                                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 border border-input rounded-md bg-background text-foreground"
                                            placeholder="Search contacts..."
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {contactsLoading ? (
                                        <div className="text-center py-4 text-muted-foreground">Loading contacts...</div>
                                    ) : filteredContacts.length === 0 ? (
                                        <div className="text-center py-4 text-muted-foreground">No contacts found</div>
                                    ) : (
                                        filteredContacts
                                            .filter(c => {
                                                // Check if contact is already in audience.contacts
                                                return !audience.contacts.some(ac => {
                                                    if (typeof ac === 'object' && ac.contactId) {
                                                        return ac.contactId === c._id && ac.contactType === 'client';
                                                    }
                                                    return String(ac) === String(c._id);
                                                });
                                            })
                                            .map(contact => (
                                                <div
                                                    key={contact._id}
                                                    className="flex items-center justify-between p-2 border border-input rounded-md hover:bg-muted cursor-pointer"
                                                    onClick={() => handleAddContact(contact._id)}
                                                >
                                                    <div>
                                                        <div className="font-medium text-foreground">{contact.name || 'Unnamed'}</div>
                                                        <div className="text-sm text-muted-foreground">{contact.email}</div>
                                                    </div>
                                                    <PlusIcon className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            ))
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-4">Segment Filters</h2>
                            <p className="text-sm text-muted-foreground mb-4">
                                Dynamic segments are automatically calculated based on filters. This feature is coming soon.
                            </p>
                            <div className="p-4 bg-muted rounded-md">
                                <p className="text-sm text-foreground">
                                    For now, use static lists to manually select contacts. Segment builder with filters will be available in a future update.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-sm font-semibold text-foreground mb-4">Audience Summary</h3>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-muted-foreground">Type</div>
                                <div className="text-sm font-medium text-foreground mt-1">
                                    {audience.type === 'static' ? (
                                        <span className="flex items-center gap-1">
                                            <UsersIcon className="h-4 w-4" />
                                            Static List
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1">
                                            <FunnelIcon className="h-4 w-4" />
                                            Dynamic Segment
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Size</div>
                                <div className="text-sm font-medium text-foreground mt-1">
                                    {audience.type === 'static'
                                        ? `${audience.contacts.length} contact${audience.contacts.length !== 1 ? 's' : ''}`
                                        : 'Calculated automatically'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSV Import Modal */}
            {csvImportStep && (
                <div 
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && csvImportStep !== 'importing') {
                            setCsvImportStep(null);
                        }
                    }}
                >
                    <div 
                        className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Upload Step */}
                        {csvImportStep === 'upload' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Import Contacts from CSV</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <div className="border-2 border-dashed border-input rounded-lg p-12 text-center">
                                    <ArrowUpTrayIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-foreground mb-2">Upload a CSV file with contact information</p>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Supported columns: Email, Name, First Name, Last Name, Phone, Company, Country, Tags
                                    </p>
                                    <p className="text-xs text-muted-foreground italic">
                                        Note: Contacts will be automatically created as leads if they don't already exist in the system.
                                    </p>
                                    <label className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer">
                                        Choose CSV File
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleCsvFileUpload}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Mapping Step */}
                        {csvImportStep === 'mapping' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Map CSV Columns</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Map each CSV column to the corresponding field. Email is required.
                                </p>
                                <div className="space-y-3 mb-4">
                                    {csvHeaders.map(header => (
                                        <div key={header} className="flex items-center gap-4">
                                            <div className="flex-1 text-sm font-medium text-foreground">
                                                {header}
                                            </div>
                                            <select
                                                value={columnMapping[header] || ''}
                                                onChange={(e) => setColumnMapping(prev => ({
                                                    ...prev,
                                                    [header]: e.target.value || undefined
                                                }))}
                                                className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                            >
                                                <option value="">-- Skip this column --</option>
                                                <option value="email">Email *</option>
                                                <option value="name">Full Name</option>
                                                <option value="firstName">First Name</option>
                                                <option value="lastName">Last Name</option>
                                                <option value="phone">Phone</option>
                                                <option value="company">Company</option>
                                                <option value="country">Country</option>
                                                <option value="tags">Tags (comma-separated)</option>
                                            </select>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setCsvImportStep('upload')}
                                        className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleColumnMapping}
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                                    >
                                        Preview Import
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Preview Step */}
                        {csvImportStep === 'preview' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Preview Import</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {mappedData.length} contact{mappedData.length !== 1 ? 's' : ''} ready to import
                                </p>
                                <div className="border border-border rounded-lg overflow-hidden mb-4">
                                    <div className="overflow-x-auto max-h-64">
                                        <table className="min-w-full divide-y divide-border">
                                            <thead className="bg-muted">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Phone</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Company</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-background divide-y divide-border">
                                                {mappedData.slice(0, 10).map((contact, index) => (
                                                    <tr key={index}>
                                                        <td className="px-3 py-2 text-sm text-foreground">{contact.email}</td>
                                                        <td className="px-3 py-2 text-sm text-foreground">
                                                            {contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-foreground">{contact.phone || '-'}</td>
                                                        <td className="px-3 py-2 text-sm text-foreground">{contact.company || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {mappedData.length > 10 && (
                                        <div className="px-3 py-2 text-xs text-muted-foreground bg-muted">
                                            ... and {mappedData.length - 10} more contact{mappedData.length - 10 !== 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setCsvImportStep('mapping')}
                                        className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleImportContacts}
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                                    >
                                        Import Contacts
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Importing Step */}
                        {csvImportStep === 'importing' && (
                            <div className="p-6">
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                                    <p className="text-foreground">Importing contacts...</p>
                                    {importResults && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                            Added: {importResults.added} | Skipped: {importResults.skipped} | Errors: {importResults.errors.length}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudienceBuilder;


